import { Box } from "@chakra-ui/react"
import { LayoutGroup, motion } from "framer-motion"
import { useLayoutEffect } from "react"
import SchoolErrorBoundary from "../error/SchoolErrorBoundary"
import { CoverPage } from "./CoverPage"
import CoverPageAugmentationToolbar from "./CoverPageAugmentationToolbar"
import CoverPageImmersiveContainer from "./CoverPageImmersive"
import CoverPageLibraryNavigation from "./CoverPageLibraryNavigation"
import CoverPageLoadingOverlay from "./CoverPageLoadingOverlay"
import CoverPageProvider from "./CoverPageProvider"
import { ANIMATION_DURATION, EASE_BEZIER } from "./animations"
import { CoverPageContainerProps } from "./types"

const MotionBox = motion(Box)

// TODO: remove this once Cover Pages is no longer in beta
const useHideCoverPagesFromSearchEngines = () => {
  // During Cover Pages beta, prevent indexing and set canonical to About
  useLayoutEffect(() => {
    const pathname = window.location?.pathname || ""
    const m = pathname.match(/^\/([^/]+)(?:\/(\d+))?\/cover\//)
    if (!m) return

    const resourceSlug = m[1]
    const cohortId = m[2]

    const aboutPath = cohortId
      ? `/library/${resourceSlug}/${cohortId}/about/`
      : `/library/${resourceSlug}/about/`

    // robots noindex
    const robotsId = "cover-pages-meta-robots"
    let robots = document.head.querySelector(
      `meta#${robotsId}`
    ) as HTMLMetaElement | null
    if (!robots) {
      robots = document.createElement("meta")
      robots.setAttribute("id", robotsId)
      robots.setAttribute("name", "robots")
      document.head.appendChild(robots)
    }
    robots.setAttribute("content", "noindex, nofollow")

    // canonical link
    const canonicalId = "cover-pages-link-canonical"
    let canonical = document.head.querySelector(
      `link#${canonicalId}`
    ) as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement("link")
      canonical.setAttribute("rel", "canonical")
      canonical.setAttribute("id", canonicalId)
      document.head.appendChild(canonical)
    }
    const baseUrl = window.location.origin || ""
    canonical.setAttribute("href", `${baseUrl}${aboutPath}`)

    return () => {
      // Clean up when leaving Cover Pages
      const r = document.head.querySelector(`meta#${robotsId}`)
      const c = document.head.querySelector(`link#${canonicalId}`)
      if (r) document.head.removeChild(r)
      if (c) document.head.removeChild(c)
    }
  }, [])
}

const CoverPageContainer = (props: CoverPageContainerProps) => {
  useHideCoverPagesFromSearchEngines()

  return (
    <SchoolErrorBoundary>
      <CoverPageProvider {...props}>
        <LayoutGroup id="cover-page">
          <MotionBox
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_DURATION.fade,
              ease: EASE_BEZIER
            }}
          >
            <CoverPage />
          </MotionBox>
          <CoverPageLoadingOverlay />
          <CoverPageImmersiveContainer {...props} />
          <CoverPageAugmentationToolbar />
          <CoverPageLibraryNavigation />
        </LayoutGroup>
      </CoverPageProvider>
    </SchoolErrorBoundary>
  )
}

export default CoverPageContainer
